import React, { useState, useMemo, useCallback } from "react";
import withNavigate from "../withNavigate";
import ReactSelect from "react-select";
import {
  BarGraphIcon1,
  BarGraphIcon2,
  BarGraphIcon3,
  DatePickerIcon,
  CloseIcon,
  ChevronDownIcon,
} from "../../../components/commonUI/Icons/SvgIcon";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import SearchFilterBar from "../../../components/commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../../components/commonUI/DataTable/DataTable";
import Pagination from "../../../components/commonUI/Pagination/Pagination";
import ChartVisualization from "../../../components/commonUI/Charts/ChartVisualization";
import { defaultAvatarImage, imageFormat, serverUrl, thumbSuffix, userImagesFolder } from "../../../config/env";
import TopNav from "../../commonUI/TopHeader/TopHeader";

const RptMembershipCancelled = (props) => {
  const [activeTab, setActiveTab] = useState("analytics");
  const [selectedPeriod, setSelectedPeriod] = useState("12M");
  const [selectedInterval, setSelectedInterval] = useState("Month");
  const [selectedChartType, setSelectedChartType] = useState("totals");
  const [chartViewMode, setChartViewMode] = useState("bar");
  const [planFilter, setPlanFilter] = useState("any");
  const [reasonFilter, setReasonFilter] = useState("any");
  const [cancelledStartDate, setCancelledStartDate] = useState(null);
  const [cancelledEndDate, setCancelledEndDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });

  // Static data for metrics
  const metricsData = {
    totalCancellations: { value: 90, change: 84, percentage: 1400 },
    monthlyPlan: { value: 68, change: 64, percentage: 1600 },
    yearlyPlan: { value: 22, change: 20, percentage: 1000 },
  };

  // Chart data for different periods
  const chartData = {
    "12M": [
      { month: "Oct 2025", total: 8, monthly: 6, yearly: 2 },
      { month: "Nov 2025", total: 7, monthly: 5, yearly: 2 },
      { month: "Dec 2025", total: 6, monthly: 4, yearly: 2 },
      { month: "Jan 2026", total: 11, monthly: 8, yearly: 3 },
      { month: "Feb 2026", total: 7, monthly: 5, yearly: 2 },
      { month: "Mar 2026", total: 8, monthly: 6, yearly: 2 },
      { month: "Apr 2026", total: 8, monthly: 6, yearly: 2 },
      { month: "May 2026", total: 7, monthly: 5, yearly: 2 },
      { month: "Jun 2026", total: 10, monthly: 7, yearly: 3 },
      { month: "Jul 2026", total: 11, monthly: 8, yearly: 3 },
      { month: "Aug 2026", total: 9, monthly: 7, yearly: 2 },
      { month: "Sep 2026", total: 2, monthly: 1, yearly: 1 },
    ],
  };

  // Static data for records table
  const recordsData = [
    {
      id: 1,
      mav: {
        name: "David Hauler",
        code: "D222E",
        initials: "DH",
        avatarColor: "teal",
      },
      cancelledOn: { date: "2026-09-10", time: "11:22" },
      reason: "Difficulty in using the app",
      plan: "Yearly",
      membershipAge: "7 months",
      accessUntil: "2026-11-10",
      earnings: "£1,254.00",
      bookings: 6,
      quotes: 27,
    },
    {
      id: 2,
      mav: {
        name: "Emma Transporter",
        code: "D222D",
        initials: "ET",
        avatarColor: "teal",
      },
      cancelledOn: { date: "2026-09-06", time: "10:11" },
      reason: "Not using it enough",
      plan: "Monthly",
      membershipAge: "6 months, 13 days",
      accessUntil: "2026-09-22",
      earnings: "£380.00",
      bookings: 2,
      quotes: 20,
    },
    {
      id: 3,
      mav: {
        name: "Michael Mover",
        code: "D222C",
        initials: "MM",
        avatarColor: "gray",
      },
      cancelledOn: { date: "2026-09-02", time: "09:00" },
      reason: "It's too expensive",
      plan: "Monthly",
      membershipAge: "5 months, 26 days",
      accessUntil: "2026-09-17",
      earnings: "£1,197.00",
      bookings: 7,
      quotes: 13,
    },
    {
      id: 4,
      mav: {
        name: "James Deliverer",
        code: "D2228",
        initials: "JD",
        avatarColor: "blue",
      },
      cancelledOn: { date: "2026-08-23", time: "14:55" },
      reason: "Other: I need to pause while my van is being repaired",
      plan: "Monthly",
      membershipAge: "3 months, 18 days",
      accessUntil: "2026-09-03",
      earnings: "£380.00",
      bookings: 4,
      quotes: 39,
    },
    {
      id: 5,
      mav: {
        name: "Sophia Trucker",
        code: "D2227",
        initials: "ST",
        avatarColor: "teal",
      },
      cancelledOn: { date: "2026-08-19", time: "13:44" },
      reason: "Difficulty in using the app",
      plan: "Monthly",
      membershipAge: "3 months, 1 day",
      accessUntil: "2026-09-16",
      earnings: "£4,182.00",
      bookings: 17,
      quotes: 32,
    },
    {
      id: 6,
      mav: {
        name: "Daniel Carrier",
        code: "D2226",
        initials: "DC",
        avatarColor: "orange",
      },
      cancelledOn: { date: "2026-08-15", time: "12:33" },
      reason: "Not using it enough",
      plan: "Yearly",
      membershipAge: "2 months, 14 days",
      accessUntil: "2026-10-15",
      earnings: "£2,497.00",
      bookings: 11,
      quotes: 25,
    },
  ];

  // Period buttons configuration
  const periodButtons = [
    { id: "custom", label: "Custom range" },
    { id: "7D", label: "7D" },
    { id: "30D", label: "30D" },
    { id: "3M", label: "3M" },
    { id: "6M", label: "6M" },
    { id: "12M", label: "12M" },
  ];

  // Interval options
  const intervalOptions = [
    { value: "Day", label: "Day" },
    { value: "Week", label: "Week" },
    { value: "Month", label: "Month" },
    { value: "Year", label: "Year" },
  ];

  // Chart type options
  const chartTypeOptions = [
    { value: "totals", label: "Totals" },
    { value: "monthly", label: "Monthly Plans" },
    { value: "yearly", label: "Yearly Plans" },
  ];

  const planOptions = [
    { value: "any", label: "Any" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const reasonOptions = [
    { value: "any", label: "Any" },
    { value: "difficulty", label: "Difficulty in using the app" },
    { value: "not_using", label: "Not using it enough" },
    { value: "expensive", label: "It's too expensive" },
    { value: "other", label: "Other" },
  ];

  const selectStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        minHeight: "40px",
        height: "40px",
        minWidth: "200px",
        width: "200px",
        padding: "0",
        boxShadow: "none",
        backgroundColor: "#FFFFFF",
        "&:focus-visible": {
          borderColor: "#000000",
          boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #000000",
          outline: "none",
        },
      }),
      valueContainer: (provided) => ({
        ...provided,
        height: "40px",
        padding: "0 8px",
        display: "flex",
        alignItems: "center",
      }),
      input: (provided) => ({
        ...provided,
        margin: "0",
        padding: "0",
      }),
      indicatorsContainer: (provided) => ({
        ...provided,
        height: "40px",
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: "#FFFFFF",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        zIndex: 1000,
      }),
      menuList: (provided) => ({
        ...provided,
        backgroundColor: "#FFFFFF",
        padding: "0",
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "#e0f2fe"
          : state.isFocused
            ? "#f1f5f9"
            : "#FFFFFF",
        color: "#333",
        cursor: "pointer",
        pointerEvents: "auto",
        padding: "10px 12px",
        "&:active": {
          backgroundColor: "#e0f2fe",
        },
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
      }),
    }),
    [],
  );

  const getAvatarSrc = useCallback((record) => {
    if (record?.mav?.avatar && record?.mav?.avatar !== "") {
      return `${serverUrl}${userImagesFolder}/${record?.mav?.shortId}/${record?.mav?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const handleInputChange = (value) => {
    setSearchValue(value);
  };

  const handleSearch = () => {
    setSearch(searchValue);
  };

  const handleReset = () => {
    setSearchValue("");
    setSearch("");
    setPlanFilter("any");
    setReasonFilter("any");
    setCancelledStartDate(null);
    setCancelledEndDate(null);
  };

  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && showDatePicker) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showDatePicker]);

  // Get current chart data based on selected period
  const getCurrentChartData = () => {
    return chartData[selectedPeriod] || chartData["12M"];
  };

  // Get chart values based on selected chart type
  const getChartValues = (data) => {
    switch (selectedChartType) {
      case "monthly":
        return data.map((item) => item.monthly);
      case "yearly":
        return data.map((item) => item.yearly);
      default:
        return data.map((item) => item.total);
    }
  };

  const handleChartMouseEnter = (event, item, value, tooltipData) => {
    const rect = event.currentTarget?.getBoundingClientRect?.() || event.target.getBoundingClientRect();
    let label = "total";
    switch (selectedChartType) {
      case "monthly":
        label = "monthly";
        break;
      case "yearly":
        label = "yearly";
        break;
      default:
        label = "total";
    }
    setTooltip({
      visible: true,
      x: event.clientX || rect.left + rect.width / 2,
      y: event.clientY || rect.top,
      data: tooltipData || { month: item.month, value, type: label },
    });
  };

  const handleChartMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, data: null });
  };

  const renderAnalyticsTab = () => (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        padding: "24px",
      }}
    >
      <div className="booking-analytics">
        {/* Time Period Controls */}
        <div className="analytics-controls">
          <div className="period-buttons-wrapper">
            <div className="custom-range-dropdown">
              <button
                className="period-btn custom-range-btn"
                onClick={() => setShowDatePicker(!showDatePicker)}
              >
                <DatePickerIcon style={{ width: "16px", height: "16px" }} />
                {cancelledStartDate && cancelledEndDate
                  ? `${moment(cancelledStartDate).format("DD MMM YYYY")} - ${moment(cancelledEndDate).format("DD MMM YYYY")}`
                  : "Custom range"}
                {cancelledStartDate && cancelledEndDate && (
                  <div
                    className="close-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCancelledStartDate(null);
                      setCancelledEndDate(null);
                    }}
                  >
                    <CloseIcon />
                  </div>
                )}
                <ChevronDownIcon style={{ marginLeft: "4px", width: "12px", height: "12px" }} />
              </button>
              {showDatePicker && (
                <div
                  className="date-picker-backdrop"
                  onClick={() => setShowDatePicker(false)}
                />
              )}
              {showDatePicker && (
                <div className="date-picker-dropdown">
                  <DatePicker
                    selectsRange
                    className="filter-box date-picker"
                    startDate={cancelledStartDate}
                    endDate={cancelledEndDate}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setCancelledStartDate(start);
                      setCancelledEndDate(end);
                      if (start && end) {
                        setShowDatePicker(false);
                      }
                    }}
                    placeholderText="Anytime"
                    monthsShown={2}
                    inline
                    showIcon={false}
                  />
                </div>
              )}
            </div>
            <div className="period-buttons">
              {periodButtons.slice(1).map((button) => (
                <button
                  key={button.id}
                  className={`period-btn ${
                    selectedPeriod === button.id ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedPeriod(button.id);
                    setShowDatePicker(false);
                  }}
                >
                  {button.label}
                </button>
              ))}
            </div>
          </div>

          <div className="interval-selector">
            <span className="interval-label">Interval:</span>
            <ReactSelect
              value={intervalOptions.find(
                (opt) => opt.value === selectedInterval,
              )}
              onChange={(option) => setSelectedInterval(option.value)}
              options={intervalOptions}
              styles={selectStyles}
              isSearchable={false}
              className="interval-select"
            />
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="metrics-cards">
          <div className="metric-card">
            <div className="metric-title">Total Cancellations</div>
            <div className="metric-value-row">
              <div className="metric-value">
                {metricsData?.totalCancellations?.value}
              </div>
              <div className="metric-change positive">
                +{metricsData?.totalCancellations?.change} (
                {metricsData?.totalCancellations?.percentage}%)
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Monthly Plan</div>
            <div className="metric-value-row">
              <div className="metric-value">
                {metricsData?.monthlyPlan?.value}
              </div>
              <div className="metric-change positive">
                +{metricsData?.monthlyPlan?.change} (
                {metricsData?.monthlyPlan?.percentage}%)
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-title">Yearly Plan</div>
            <div className="metric-value-row">
              <div className="metric-value">
                {metricsData?.yearlyPlan?.value}
              </div>
              <div className="metric-change positive">
                +{metricsData?.yearlyPlan?.change} (
                {metricsData?.yearlyPlan?.percentage}%)
              </div>
            </div>
          </div>
        </div>

        {/* Chart Section */}
        <div
          className="chart-section"
          style={{ background: "transparent", border: "none", padding: "0" }}
        >
          <div className="chart-controls">
            <ReactSelect
              value={chartTypeOptions.find(
                (opt) => opt.value === selectedChartType,
              )}
              onChange={(option) => setSelectedChartType(option.value)}
              options={chartTypeOptions}
              styles={selectStyles}
              isSearchable={false}
              className="chart-select"
            />
            <div className="chart-view-options">
              <button
                className={`chart-btn ${chartViewMode === "bar" ? "active" : ""}`}
                onClick={() => setChartViewMode("bar")}
                title="Bar Chart"
              >
                <BarGraphIcon1 />
              </button>
              <button
                className={`chart-btn ${chartViewMode === "area" ? "active" : ""}`}
                onClick={() => setChartViewMode("area")}
                title="Area Chart"
              >
                <BarGraphIcon2 />
              </button>
              <button
                className={`chart-btn ${
                  chartViewMode === "line" ? "active" : ""
                }`}
                onClick={() => setChartViewMode("line")}
                title="Line Chart"
              >
                <BarGraphIcon3 />
              </button>
            </div>
          </div>

          <div className="chart-content">
            {chartViewMode === "bar" && (
              <ChartVisualization
                data={getCurrentChartData()}
                chartType={`bar-${selectedChartType}`}
                tooltip={tooltip}
                onBarMouseEnter={handleChartMouseEnter}
                onBarMouseLeave={handleChartMouseLeave}
              />
            )}
            {chartViewMode === "line" && (
              <ChartVisualization
                data={getCurrentChartData()}
                chartType={`line-${selectedChartType}`}
                tooltip={tooltip}
                onBarMouseEnter={handleChartMouseEnter}
                onBarMouseLeave={handleChartMouseLeave}
              />
            )}
            {chartViewMode === "area" && (
              <ChartVisualization
                data={getCurrentChartData()}
                chartType={`area-${selectedChartType}`}
                tooltip={tooltip}
                onBarMouseEnter={handleChartMouseEnter}
                onBarMouseLeave={handleChartMouseLeave}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const filters = [
    {
      type: "select",
      key: "plan",
      label: "Plan",
      value: planFilter,
      onChange: setPlanFilter,
      placeholder: "Any",
      options: planOptions,
    },
    {
      type: "select",
      key: "reason",
      label: "Reason",
      value: reasonFilter,
      onChange: setReasonFilter,
      placeholder: "Any",
      options: reasonOptions,
    },
    {
      type: "dateRange",
      key: "cancelledDate",
      label: "Cancelled date",
      value: { start: cancelledStartDate, end: cancelledEndDate },
      onChange: (dates) => {
        setCancelledStartDate(dates.start);
        setCancelledEndDate(dates.end);
      },
      placeholder: "Anytime",
    },
  ];

  const columns = [
    { key: "mav", label: "MAV" },
    { key: "cancelledOn", label: "Cancelled on" },
    { key: "reason", label: "Reason" },
    { key: "plan", label: "Plan" },
    { key: "membershipAge", label: "Membership age" },
    { key: "accessUntil", label: "Access until" },
    { key: "earnings", label: "Earnings" },
    { key: "bookings", label: "Bookings" },
    { key: "quotes", label: "Quotes" },
    { key: "action", label: "" },
  ];

  const renderRow = useCallback(
    (record) => (
      <tr key={record.id}>
        <td>
          <div className="d-flex align-items-center g-12">
            <img
              className="avatar-pic"
              alt="avatar"
              src={getAvatarSrc(record)}
              onError={(e) => {
                e.target.src = defaultAvatarImage;
              }}
            />
            <div className="text-capitalize">
              {record?.mav?.name || "-"}
              <br />
              <span className="text-sm text-gray-600 ">
                {record?.mav?.code}
              </span>
            </div>
          </div>
        </td>
        <td>
          {moment(record?.cancelledOn?.date)
            ?.tz("Europe/London")
            ?.format("DD MMM YY")}
          <br />
          <span className="text-sm text-gray-600">
            {moment(record?.cancelledOn?.date)
              ?.tz("Europe/London")
              ?.format("HH:mm")}
          </span>
        </td>
        <td>{record.reason}</td>
        <td>{record.plan}</td>
        <td>{record.membershipAge}</td>
        <td>{record.accessUntil}</td>
        <td>{record.earnings}</td>
        <td>{record.bookings}</td>
        <td>{record.quotes}</td>
        <td>
          <button className="custom-btn">View profile</button>
        </td>
      </tr>
    ),
    [getAvatarSrc],
  );

  const renderRecordsTab = () => (
    <div>
      <SearchFilterBar
        searchValue={searchValue}
        onSearchChange={handleInputChange}
        onSearchSubmit={handleSearch}
        searchPlaceholder="Search by MAV or reason..."
        filters={filters}
        onReset={handleReset}
      />

      <div className="mb-24">
        <div>Showing {recordsData.length} results</div>
      </div>

      <DataTable
        data={recordsData}
        columns={columns}
        renderRow={renderRow}
        onSort={() => {}}
        getSortClass={() => ""}
        emptyMessage="No records found"
        colSpan={10}
      />

      <Pagination
        pageSize={pageSize}
        totalPages={totalPages}
        page={page}
        setPage={setPage}
        inputValue={inputValue}
        setInputValue={setInputValue}
        setPageSize={setPageSize}
      />
    </div>
  );

  return (
    <>
      <TopNav title="Membership Cancellations" />
      <div className="global-body">
        <div className="global-body-container">
          <div>
            {/* Tab Navigation */}
            <div className="custom-tabs mt-0">
              <button
                className={`tab-item ${
                  activeTab === "analytics" ? "active" : ""
                }`}
                onClick={() => setActiveTab("analytics")}
              >
                Analytics
              </button>
              <button
                className={`tab-item ${
                  activeTab === "records" ? "active" : ""
                }`}
                onClick={() => setActiveTab("records")}
              >
                Records
              </button>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === "analytics"
                ? renderAnalyticsTab()
                : renderRecordsTab()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withNavigate(RptMembershipCancelled);
